<!--
 * @Author: Tahiti
 * @Date: 2025-12-17
 * @Description: 图片特效卡片组件（图片上标题下，固定高度）
 * @FilePath: /weryai-web/components/Effects/PhotoEffectsCard.vue
-->
<template>
    <div
        class="photo-effects-card cursor-pointer overflow-hidden rounded-xl"
        :style="
            autoSize
                ? { width: '100%', height: '100%' }
                : { width: `${width}px` }
        "
        :class="{ 'flex flex-col': autoSize }"
        @click="handleClick"
    >
        <div
            class="card-image relative overflow-hidden group"
            :class="{ 'flex-1': autoSize }"
            :style="
                autoSize
                    ? { width: '100%' }
                    : {
                          width: `${width}px`,
                          height: `${width - titleHeight}px`,
                      }
            "
        >
            <!-- thumbnail -->
            <img
                :src="coverThumbnail || item.cover"
                :alt="item.title"
                loading="lazy"
                decoding="async"
                class="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-110"
                :style="{
                    opacity: !isCoverLoaded && !isCoverThumbError ? 1 : 0,
                }"
                @error="isCoverThumbError = true"
            />
            <!-- origin -->
            <img
                :src="item.cover"
                :alt="item.title"
                loading="lazy"
                decoding="async"
                class="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-110"
                :style="{
                    // 缩略图失败时直接展示原图层，避免两层都透明导致空白
                    opacity:
                        (isCoverLoaded || isCoverThumbError) &&
                        !isCoverOriginError
                            ? 1
                            : 0,
                }"
                @load="handleCoverLoaded"
                @error="handleCoverError"
            />

            <!-- hot/likes -->
            <div
                v-if="item.likes"
                class="absolute top-3 right-3 z-[2] flex items-center justify-center gap-1 text-white text-xs rounded-md"
                style="
                    width: 58px;
                    height: 32px;
                    background: rgba(0, 0, 0, 0.6);
                "
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                >
                    <path
                        d="M12.1108 4.03674C10.2908 4.58491 9.95763 6.13877 10.075 7.12569C8.77669 5.51397 8.83 3.65991 8.83 0.98877C4.66605 2.64689 5.63443 7.42763 5.5099 8.8794C4.46255 7.97408 4.26465 5.81096 4.26465 5.81096C3.15902 6.41179 2.60461 8.01638 2.60461 9.31794C2.60461 12.4654 5.02022 15.0168 7.99997 15.0168C10.9797 15.0168 13.3953 12.4654 13.3953 9.31794C13.3953 7.44747 12.0951 6.58464 12.1108 4.03674Z"
                        fill="white"
                    />
                </svg>
                {{ formatNumber(item.likes) }}
            </div>

            <!-- use this template button -->
            <button
                class="absolute bottom-4 left-1/2 -translate-x-1/2 z-[3] py-2 px-6 bg-[#99F768] text-[#000] text-sm font-normal leading-normal rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
                @click.stop="handleUseTemplate"
            >
                {{ t('menus.useThisTemplate') }}
            </button>
        </div>

        <!-- title -->
        <div
            v-if="item.title"
            class="card-title max-w-full !inline-block leading-[44px] truncate"
        >
            {{ item.title }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { getResourceThumbnail } from '~/utils/common'

interface EffectItem {
    id: string | number
    cover?: string
    title?: string
    likes?: number
}

const props = withDefaults(
    defineProps<{
        item: EffectItem
        width?: number
        height?: number
        autoSize?: boolean // 自适应容器尺寸（移动端使用）
    }>(),
    {
        width: 240,
        height: 320,
        autoSize: false,
    }
)

const emit = defineEmits<{
    click: [item: EffectItem]
    useTemplate: [item: EffectItem]
}>()

const { t } = useI18n()

// 原图是否已加载完成（控制两层 img 切换，保证不闪）
const isCoverLoaded = ref(false)
const isCoverThumbError = ref(false)
const isCoverOriginError = ref(false)

const coverThumbnail = computed(() => {
    return props?.item?.cover ? getResourceThumbnail(props.item.cover) : ''
})

/**
 * 核心修复点：
 * SSR/水合时，图片可能在事件监听挂上前就已经 load 完成（尤其命中缓存），
 * 导致模板里的 @load 不触发，状态永远不更新。
 * 因此这里用 new Image() 主动预加载原图，确保一定能触发回调/complete 检测。
 */
const preloadCoverOrigin = () => {
    if (!import.meta.client) return
    const src = props.item.cover
    if (!src) return

    // 已经成功就不重复
    if (isCoverLoaded.value) return

    const img = new Image()
    img.src = src

    // 命中缓存：同步可用
    if (img.complete && img.naturalWidth > 0) {
        isCoverLoaded.value = true
        isCoverOriginError.value = false
        return
    }

    img.onload = () => {
        isCoverLoaded.value = true
        isCoverOriginError.value = false
    }
    img.onerror = () => {
        isCoverLoaded.value = false
        isCoverOriginError.value = true
    }
}

const handleCoverLoaded = () => {
    isCoverLoaded.value = true
    isCoverOriginError.value = false
}

const handleCoverError = () => {
    isCoverLoaded.value = false
    isCoverOriginError.value = true
}

watch(
    () => props.item.cover,
    () => {
        isCoverLoaded.value = false
        isCoverThumbError.value = false
        isCoverOriginError.value = false
        preloadCoverOrigin()
    }
)

onMounted(() => {
    preloadCoverOrigin()
})

// 标题区域高度（用于计算图片高度，使整体卡片为正方形）
const titleHeight = 44

// 格式化数字
const formatNumber = (num: number): string => {
    if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
    }
    return num.toString()
}

// 点击卡片
const handleClick = () => {
    emit('click', props.item)
}

// 点击使用模板
const handleUseTemplate = () => {
    emit('useTemplate', props.item)
}
</script>

<style lang="less" scoped>
.photo-effects-card {
    background: #22242d;
}

.card-image {
    background: #1b1d25;
}

.card-title {
    height: 44px;
    display: flex;
    align-items: center;
    padding: 0 12px;
    background: #22242d;
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
